
import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    section: {
        marginTop: 10,
        padding: 8,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#cccccc',
        borderRadius: 3,
    },
    sectionTitle: {
        fontSize: 12,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 6,
        backgroundColor: '#f0f0f0',
        padding: 4,
        borderRadius: 2,
        textAlign: 'center',
    },
    subsectionTitle: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 4,
        marginTop: 6,
        color: '#495057',
    },
    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        marginBottom: 6,
    },
    checkItem: {
        width: '32%',
        marginBottom: 3,
        flexDirection: 'row',
        alignItems: 'center',
    },
    checkboxContainer: {
        width: 10,
        height: 10,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#495057',
        marginRight: 6,
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkboxChecked: {
        backgroundColor: '#495057',
    },
    checkMark: {
        fontSize: 7,
        color: '#ffffff',
        fontFamily: 'Helvetica-Bold',
    },
    label: {
        fontSize: 9,
        fontFamily: 'Helvetica',
        marginRight: 4,
        color: '#495057',
        flex: 1,
    },
    textOnlyItem: {
        width: '32%',
        marginBottom: 3,
        flexDirection: 'row',
        alignItems: 'center',
    },
    textOnlyLabel: {
        fontSize: 9,
        fontFamily: 'Helvetica',
        color: '#495057',
    },
    value: {
        fontSize: 9,
        fontFamily: 'Helvetica',
        color: '#495057',
        marginLeft: 2,
    },
    notesSection: {
        marginTop: 6,
        padding: 4,
        backgroundColor: '#f8f9fa',
        borderRadius: 2,
    },
    notesTitle: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 3,
        color: '#495057',
    },
    notesText: {
        fontSize: 9,
        fontFamily: 'Helvetica',
        color: '#6c757d',
        lineHeight: 1.3,
    },
});

interface FinalCheckSectionProps {
    inspectionSheet: {
        fans?: boolean;
        climaCell?: boolean;
        humidifiers?: boolean;
        humidifierMode?: string;
        controlPanel?: boolean;
        plenumDucts?: boolean;
        refrigeration?: boolean;
        doorsLouvers?: boolean;
        visualInspectionNotes?: string;
    };
}

interface CheckboxProps {
    checked?: boolean;
}

const FinalCheckSection: React.FC<FinalCheckSectionProps> = ({ inspectionSheet }) => {
    const Checkbox: React.FC<CheckboxProps> = ({ checked }) => (
        <View style={checked ? [styles.checkboxContainer, styles.checkboxChecked] : styles.checkboxContainer}>
            {checked && <Text style={styles.checkMark}>✓</Text>}
        </View>
    );

    return (
        <View style={styles.section}>
            <Text style={styles.sectionTitle}>Final Check</Text>
            <View style={styles.grid}>
                <View style={styles.checkItem}>
                    <Checkbox checked={inspectionSheet.fans} />
                    <Text style={styles.label}>Fans</Text>
                </View>
                <View style={styles.checkItem}>
                    <Checkbox checked={inspectionSheet.climaCell} />
                    <Text style={styles.label}>ClimaCell</Text>
                </View>
                <View style={styles.checkItem}>
                    <Checkbox checked={inspectionSheet.humidifiers} />
                    <Text style={styles.label}>Humidifiers</Text>
                </View>
                {(!!inspectionSheet.humidifiers && typeof inspectionSheet.humidifierMode === 'string' && inspectionSheet.humidifierMode.trim() !== '') ? (
                    <View style={styles.textOnlyItem}>
                        <Text style={styles.textOnlyLabel}>Humidifier Mode:</Text>
                        <Text style={styles.value}>{inspectionSheet.humidifierMode}</Text>
                    </View>
                ) : null}
                <View style={styles.checkItem}>
                    <Checkbox checked={inspectionSheet.controlPanel} />
                    <Text style={styles.label}>Control Panel</Text>
                </View>
                <View style={styles.checkItem}>
                    <Checkbox checked={inspectionSheet.plenumDucts} />
                    <Text style={styles.label}>Plenum & Ducts</Text>
                </View>
                <View style={styles.checkItem}>
                    <Checkbox checked={inspectionSheet.refrigeration} />
                    <Text style={styles.label}>Refrigeration</Text>
                </View>
                <View style={styles.checkItem}>
                    <Checkbox checked={inspectionSheet.doorsLouvers} />
                    <Text style={styles.label}>Doors & Louvers</Text>
                </View>
            </View>

            {(typeof inspectionSheet.visualInspectionNotes === 'string' && inspectionSheet.visualInspectionNotes.trim() !== '') ? (
                <View style={styles.notesSection}>
                    <Text style={styles.notesTitle}>Visual Inspection Notes:</Text>
                    <Text style={styles.notesText}>{inspectionSheet.visualInspectionNotes}</Text>
                </View>
            ) : null}
        </View>
    );
};

export default FinalCheckSection;
